import { useContext } from "react";
import { useRouter } from "next/router";

import { ModalContext } from "@/contexts/ModalsContext";

import { FetchOptions, FetchHeaders } from "@/types/HTTP.types";
import { NotifiTypes } from "@/types/UX.types";

type Result<T = any> = {
    success: boolean;
    statusCode: number;
    data: T;
    message?: {
        text: string;
        type: NotifiTypes;
    };
};

type Overload = {
    (url: string, options?: FetchOptions, resetNotification?: boolean): Promise<Result>;
    <T>(url: string, options?: FetchOptions, resetNotification?: boolean): Promise<Result<T>>;
};

const jsonMethods: string[] = ["PUT", "POST", "PATCH"];

const defaultOptions: FetchOptions = {
    method: "GET"
};

const paths: string[] = ["/admins", "/tokens"];

const useFetch = () => {
    const { dispatchModal } = useContext(ModalContext);
    const router = useRouter();

    const useFetchData: Overload = async <T,>(
        url: string, 
        options: FetchOptions = defaultOptions, 
        resetNotification: boolean = false
    ): Promise<Result<T>> => {
        let redirect: boolean = false;
        let signOut: boolean = false;
        let reqHeaders: FetchHeaders = { ...options.headers };

        if (!reqHeaders?.["Content-Type"] && jsonMethods.includes(options.method)) {
            reqHeaders = {...reqHeaders, "Content-Type": "application/json"};
        }

        if (reqHeaders?.["Content-Type"] === "multipart/form-data") {
            delete reqHeaders["Content-Type"];
        }

        const fetchOptions: FetchOptions = {...options, credentials: "include", headers: reqHeaders};

        const response = await fetch(url, fetchOptions);

        if (resetNotification) {
            dispatchModal({ type: "SET_NOTIFI", notifi: null });
        }

        if (response.status === 401) {
            signOut = true;
        }

        if (response.status === 403) {
            redirect = true;
        }

        const result: Result<T> = await response.json();

        if (result.message) {
            dispatchModal({ 
                type: "SET_NOTIFI",
                notifi: {
                    message: result.message.text,
                    notifiType: result.message.type,
                    show: true
                } 
            });
        }

        if (redirect && paths.includes(router.pathname)) {
            window.location.href = "/dashboard";
        }

        if (signOut && router.pathname !== '/') {
            window.location.href = '/';
        }

        return result;
    };

    return {
        useFetchData
    };
};

export default useFetch;

